import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, Observable, firstValueFrom } from 'rxjs';
import { takeUntil, map, switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { PreAlertService } from '../../services/pre-alert.service';
import { ExcelUploadComponent, ExcelUploadResult, UploadProgress } from '../excel-upload/excel-upload.component';
import { ExcelImportService, UploadProgressEvent } from '../../services/excel-import.service';
import { ImportJobDto, ImportJobStatusDto } from '../../../models/import-job.dto';
import { PreAlertInputModalComponent, PreAlertInputData, PreAlertModalResult } from '../modals/pre-alert-input-modal/pre-alert-input-modal.component';
import * as XLSX from 'xlsx';

export interface ShopeeDocValidationRule {
  columnName: string;
  required: boolean;
  dataType: 'string' | 'number' | 'date' | 'email';
  pattern?: RegExp;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
}

export interface ShopeeUploadConfig {
  documentType: 'order' | 'shipment' | 'manifest' | 'customs' | 'lm';
  requiredColumns: string[];
  validationRules: ShopeeDocValidationRule[];
  maxFileSize: number;
  batchSize: number;
  allowDuplicates: boolean;
}

export interface ValidationError {
  row: number;
  column: string;
  value: any;
  message: string;
  severity: 'error' | 'warning';
}

export interface ProcessedUploadResult extends ExcelUploadResult {
  validationErrors: ValidationError[];
  validRowCount: number;
  invalidRowCount: number;
  duplicateCount: number;
  processedData: any[];
  summary: {
    totalRows: number;
    successRate: number;
    warnings: number;
    errors: number;
  };
  previewData?: any[];
  requiresPreAlert?: boolean;
}

export interface LMPreviewData {
  headers: string[];
  rows: any[][];
  totalRows: number;
  filename: string;
  validationPassed: boolean;
  errors: string[];
  uniqueCartonCount?: number;
}

export interface UploadHistoryItem {
  id: string;
  filename: string;
  documentType: string;
  uploadedAt: Date;
  uploadedBy: string;
  totalRows: number;
  validRows: number;
  status: 'success' | 'warning' | 'error';
  processingTime: number;
  fileSize: number;
}

export interface ShopeeFormatTemplate {
  documentType: string;
  requiredColumns: string[];
  sampleData: any[];
  description: string;
  version: string;
}

@Component({
  selector: 'app-shopee-excel-upload',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ExcelUploadComponent, PreAlertInputModalComponent],
  templateUrl: './shopee-excel-upload.component.html',
  styleUrl: './shopee-excel-upload.component.css'
})
export class ShopeeExcelUploadComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() config: ShopeeUploadConfig = {
    documentType: 'lm',
    requiredColumns: [],
    validationRules: [],
    maxFileSize: 25 * 1024 * 1024, // 25MB for Shopee docs
    batchSize: 1000,
    allowDuplicates: false
  };

  @Input() disabled: boolean = false;
  @Input() showValidationSummary: boolean = true;
  @Input() showPreview: boolean = true;
  @Input() maxPreviewRows: number = 10;
  @Input() showUploadHistory: boolean = true;
  @Input() maxHistoryItems: number = 20;
  @Input() enableTemplateDownload: boolean = true;
  @Input() enableBulkValidation: boolean = true;

  @Output() uploadStarted = new EventEmitter<File>();
  @Output() validationCompleted = new EventEmitter<ProcessedUploadResult>();
  @Output() uploadCompleted = new EventEmitter<ProcessedUploadResult>();
  @Output() uploadError = new EventEmitter<string>();
  @Output() historyItemClicked = new EventEmitter<UploadHistoryItem>();
  @Output() templateDownloaded = new EventEmitter<string>();
  @Output() apiJobStarted = new EventEmitter<ImportJobDto>();
  @Output() apiJobCompleted = new EventEmitter<ImportJobDto>();
  @Output() preAlertRequested = new EventEmitter<LMPreviewData>();
  @Output() preAlertSaved = new EventEmitter<{ preAlert: PreAlertInputData; fileData: LMPreviewData }>();

  // Role-based access
  hasShopeeAccess$: Observable<boolean>;
  userRole$: Observable<string | null>;

  // Upload state
  uploadForm: FormGroup;
  isUploading = false;
  currentProgress: UploadProgress | null = null;
  selectedFile: File | null = null;
  processedResult: ProcessedUploadResult | null = null;
  error: string | null = null;
  
  // API Integration state
  useApiUpload = false;
  currentJob: ImportJobDto | null = null;
  jobStatus: ImportJobStatusDto | null = null;
  apiProgress: UploadProgressEvent | null = null;
  isPollingStatus = false;
  
  // Upload history
  uploadHistory: UploadHistoryItem[] = [];
  
  // UI state
  showAdvancedOptions = false;
  showErrorDetails = false;
  showHistoryPanel = false;
  dragOverCounter = 0;
  
  // Template management
  availableTemplates: ShopeeFormatTemplate[] = [];
  selectedTemplate: ShopeeFormatTemplate | null = null;
  
  // Performance tracking
  uploadStartTime: Date | null = null;
  processingStartTime: Date | null = null;

  // Pre-alert modal state
  showPreAlertModal = false;
  preAlertModalTitle = 'Pre-Alert Information';
  lmPreviewData: LMPreviewData | null = null;
  isPreAlertLoading = false;
  preAlertModalMode: 'create' | 'edit' = 'create';
  
  // Success state
  successMessage: string | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private preAlertService: PreAlertService,
    private excelImportService: ExcelImportService,
    private router: Router
  ) {
    this.uploadForm = this.createForm();
    this.hasShopeeAccess$ = this.authService.hasRole$('shopee_docs');
    this.userRole$ = this.authService.userRole$;
  }

  ngOnInit(): void {
    // Check role-based access
    this.hasShopeeAccess$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(hasAccess => {
      if (!hasAccess) {
        this.disabled = true;
        this.error = 'Access denied. This feature requires ShopeeDocsTeam role.';
      }
    });

    // Set default validation rules based on document type
    this.setDefaultValidationRules();
    
    // Initialize available templates
    this.initializeTemplates();
    
    // Load upload history
    this.loadUploadHistory();
    
    // Initialize useApiUpload from form value
    this.useApiUpload = this.uploadForm.get('useApiUpload')?.value ?? true;
    
    // Subscribe to form changes
    this.uploadForm.get('useApiUpload')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(useApi => {
      this.useApiUpload = useApi;
      if (!useApi) {
        // Clear API-related state when switching back to local processing
        this.currentJob = null;
        this.jobStatus = null;
        this.apiProgress = null;
        this.stopPolling();
      }
    });
    
    // Subscribe to API upload progress
    this.excelImportService.progress$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(progress => {
      this.apiProgress = progress;
      if (progress) {
        this.currentProgress = {
          percentage: progress.percentage,
          stage: progress.type === 'upload' ? 'reading' : 'processing',
          message: progress.message || ''
        };
      }
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.excelImportService.clearProgress();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      documentType: [this.config.documentType, [Validators.required]],
      validateData: [true],
      skipEmptyRows: [true],
      strictValidation: [false],
      useApiUpload: [true],
      notes: ['']
    });
  }

  private setDefaultValidationRules(): void {
    switch (this.config.documentType) {
      case 'order':
        this.config.requiredColumns = ['Order ID', 'Product Name', 'Quantity', 'Price'];
        this.config.validationRules = [
          { columnName: 'Order ID', required: true, dataType: 'string', maxLength: 50 },
          { columnName: 'Quantity', required: true, dataType: 'number', minValue: 1 },
          { columnName: 'Price', required: true, dataType: 'number', minValue: 0 }
        ];
        break;
      case 'shipment':
        this.config.requiredColumns = ['Tracking Number', 'Weight', 'Dimensions'];
        this.config.validationRules = [
          { columnName: 'Tracking Number', required: true, dataType: 'string', maxLength: 30 },
          { columnName: 'Weight', required: true, dataType: 'number', minValue: 0.1, maxValue: 30000 }
        ];
        break;
      case 'manifest':
        this.config.requiredColumns = ['Manifest ID', 'Container Number', 'Weight'];
        this.config.validationRules = [
          { columnName: 'Manifest ID', required: true, dataType: 'string' },
          { columnName: 'Weight', required: true, dataType: 'number', minValue: 0 }
        ];
        break;
      case 'lm':
        this.config.requiredColumns = [
          'LM Tracking', 
          'Shopee Tracking', 
          'Carton No', 
          'Carton Size', 
          'Parcel weight', 
          'Carton Weight', 
          'Parcel Size', 
          'Channel', 
          'Service Code', 
          'Country', 
          'Destination Code'
        ];
        this.config.validationRules = [
          { columnName: 'LM Tracking', required: true, dataType: 'string', maxLength: 50 },
          { columnName: 'Shopee Tracking', required: false, dataType: 'string', maxLength: 50 },
          { columnName: 'Carton No', required: true, dataType: 'string', maxLength: 30 },
          { columnName: 'Carton Weight', required: true, dataType: 'number', minValue: 0.1, maxValue: 30000 },
          { columnName: 'Parcel weight', required: true, dataType: 'number', minValue: 0.1, maxValue: 30000 }
        ];
        break;
      default:
        // Default validation rules
        break;
    }
  }

  onFileSelected(file: File): void {
    this.selectedFile = file;
    this.error = null;
    this.processedResult = null;
    this.currentJob = null;
    this.jobStatus = null;
    this.uploadStartTime = new Date();
    this.uploadStarted.emit(file);
    
    // Show file selection feedback
    this.showFileSelectedFeedback(file);
    
    // Check if this is an LM file that needs pre-alert input
    if (this.config.documentType === 'lm') {
      // LM files always need pre-alert input first, regardless of API upload setting
      this.handleLMFilePreview(file);
    } else if (this.useApiUpload) {
      this.processWithAPI(file);
    }
  }

  private showFileSelectedFeedback(file: File): void {
    const fileSize = (file.size / (1024 * 1024)).toFixed(1);
    const message = `Selected: ${file.name} (${fileSize} MB)`;
    
    // You could integrate with a toast notification service here
    console.log(message);
  }

  onUploadProgress(progress: UploadProgress): void {
    this.currentProgress = progress;
  }

  onUploadComplete(result: ExcelUploadResult): void {
    if (!this.useApiUpload) {
      this.processExcelData(result);
    }
  }

  onUploadError(error: string): void {
    this.error = error;
    this.isUploading = false;
    this.uploadError.emit(error);
  }

  private async processExcelData(result: ExcelUploadResult): Promise<void> {
    try {
      this.isUploading = true;
      this.processingStartTime = new Date();
      
      // Update progress - Header validation
      this.currentProgress = {
        percentage: 25,
        stage: 'validating',
        message: 'Validating Shopee document format...'
      };
      this.onUploadProgress(this.currentProgress);

      // Validate headers
      const headerValidation = this.validateHeaders(result.headers);
      if (!headerValidation.isValid) {
        throw new Error(headerValidation.message);
      }

      // Update progress - Data validation
      this.currentProgress = {
        percentage: 50,
        stage: 'validating', 
        message: `Validating ${result.rowCount} rows of data...`
      };
      this.onUploadProgress(this.currentProgress);

      // Process and validate data with progress updates
      const validationResults = await this.validateDataWithProgress(result.data, result.headers);
      
      // Update progress - Finalizing
      this.currentProgress = {
        percentage: 90,
        stage: 'processing',
        message: 'Finalizing results...'
      };
      this.onUploadProgress(this.currentProgress);
      
      // Create processed result
      const processedResult: ProcessedUploadResult = {
        ...result,
        validationErrors: validationResults.errors,
        validRowCount: validationResults.validRows,
        invalidRowCount: validationResults.invalidRows,
        duplicateCount: validationResults.duplicates,
        processedData: validationResults.processedData,
        summary: {
          totalRows: result.rowCount,
          successRate: result.rowCount > 0 ? (validationResults.validRows / result.rowCount) * 100 : 0,
          warnings: validationResults.errors.filter(e => e.severity === 'warning').length,
          errors: validationResults.errors.filter(e => e.severity === 'error').length
        }
      };

      this.processedResult = processedResult;
      this.isUploading = false;
      
      // Update final progress
      this.currentProgress = {
        percentage: 100,
        stage: 'complete',
        message: `Processing complete! ${validationResults.validRows}/${result.rowCount} rows valid (${processedResult.summary.successRate.toFixed(1)}%)`
      };
      this.onUploadProgress(this.currentProgress);

      // Save to history
      this.saveToUploadHistory(processedResult);

      // Emit events
      this.validationCompleted.emit(processedResult);
      this.uploadCompleted.emit(processedResult);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process Shopee document';
      this.error = errorMessage;
      this.isUploading = false;
      this.uploadError.emit(errorMessage);
    }
  }

  private validateHeaders(headers: string[]): { isValid: boolean; message: string } {
    const missingColumns = this.config.requiredColumns.filter(required => 
      !headers.some(header => header?.toLowerCase().trim() === required.toLowerCase())
    );

    if (missingColumns.length > 0) {
      return {
        isValid: false,
        message: `Missing required columns: ${missingColumns.join(', ')}`
      };
    }

    return { isValid: true, message: '' };
  }

  private async validateDataWithProgress(data: any[][], headers: string[]): Promise<{
    errors: ValidationError[];
    validRows: number;
    invalidRows: number;
    duplicates: number;
    processedData: any[];
  }> {
    const batchSize = 100; // Process in batches for better performance
    const errors: ValidationError[] = [];
    const processedData: any[] = [];
    const seenRows = new Set<string>();
    let validRows = 0;
    let invalidRows = 0;
    let duplicates = 0;

    const formValues = this.uploadForm.value;
    const skipEmptyRows = formValues.skipEmptyRows;
    const strictValidation = formValues.strictValidation;

    // Process data in batches to avoid blocking UI
    for (let batchStart = 0; batchStart < data.length; batchStart += batchSize) {
      const batchEnd = Math.min(batchStart + batchSize, data.length);
      const batch = data.slice(batchStart, batchEnd);
      
      // Update progress for this batch
      const progressPercentage = 50 + Math.round(((batchStart / data.length) * 40));
      this.currentProgress = {
        percentage: progressPercentage,
        stage: 'validating',
        message: `Validating rows ${batchStart + 1}-${batchEnd} of ${data.length}...`
      };
      this.onUploadProgress(this.currentProgress);
      
      // Process batch
      const batchResult = await this.processBatch(
        batch, 
        headers, 
        batchStart, 
        skipEmptyRows, 
        strictValidation, 
        seenRows
      );
      
      errors.push(...batchResult.errors);
      processedData.push(...batchResult.processedData);
      validRows += batchResult.validRows;
      invalidRows += batchResult.invalidRows;
      duplicates += batchResult.duplicates;
      
      // Allow UI to update between batches
      await this.delay(1);
    }

    return {
      errors,
      validRows,
      invalidRows,
      duplicates,
      processedData
    };
  }

  private async processBatch(
    batch: any[][],
    headers: string[],
    startIndex: number,
    skipEmptyRows: boolean,
    strictValidation: boolean,
    seenRows: Set<string>
  ): Promise<{
    errors: ValidationError[];
    validRows: number;
    invalidRows: number;
    duplicates: number;
    processedData: any[];
  }> {
    const errors: ValidationError[] = [];
    const processedData: any[] = [];
    let validRows = 0;
    let invalidRows = 0;
    let duplicates = 0;

    for (let i = 0; i < batch.length; i++) {
      const rowIndex = startIndex + i;
      const row = batch[i];
      const rowObject: any = {};
      let hasValidData = false;
      let hasErrors = false;

      // Skip empty rows if configured
      if (skipEmptyRows && this.isEmptyRow(row)) {
        continue;
      }

      // Convert row array to object using headers
      for (let colIndex = 0; colIndex < headers.length; colIndex++) {
        const header = headers[colIndex];
        const value = row[colIndex];
        
        if (header) {
          rowObject[header] = value;
          if (value !== undefined && value !== null && value !== '') {
            hasValidData = true;
          }
        }
      }

      if (!hasValidData && skipEmptyRows) {
        continue;
      }

      // Check for duplicates
      if (!this.config.allowDuplicates) {
        const rowKey = JSON.stringify(rowObject);
        if (seenRows.has(rowKey)) {
          duplicates++;
          errors.push({
            row: rowIndex + 2, // +2 because 1-indexed and headers
            column: 'ALL',
            value: rowObject,
            message: 'Duplicate row detected',
            severity: 'warning'
          });
          continue;
        }
        seenRows.add(rowKey);
      }

      // Validate against rules
      for (const rule of this.config.validationRules) {
        const value = rowObject[rule.columnName];
        const validationError = this.validateCell(value, rule, rowIndex + 2, rule.columnName);
        
        if (validationError) {
          errors.push(validationError);
          if (validationError.severity === 'error') {
            hasErrors = true;
          }
        }
      }

      // Count valid/invalid rows
      if (hasErrors && strictValidation) {
        invalidRows++;
      } else {
        validRows++;
        processedData.push(rowObject);
      }
    }

    return {
      errors,
      validRows,
      invalidRows,
      duplicates,
      processedData
    };
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async validateData(data: any[][], headers: string[]): Promise<{
    errors: ValidationError[];
    validRows: number;
    invalidRows: number;
    duplicates: number;
    processedData: any[];
  }> {
    const errors: ValidationError[] = [];
    const processedData: any[] = [];
    const seenRows = new Set<string>();
    let validRows = 0;
    let invalidRows = 0;
    let duplicates = 0;

    const formValues = this.uploadForm.value;
    const skipEmptyRows = formValues.skipEmptyRows;
    const strictValidation = formValues.strictValidation;

    for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
      const row = data[rowIndex];
      const rowObject: any = {};
      let hasValidData = false;
      let hasErrors = false;

      // Skip empty rows if configured
      if (skipEmptyRows && this.isEmptyRow(row)) {
        continue;
      }

      // Convert row array to object using headers
      for (let colIndex = 0; colIndex < headers.length; colIndex++) {
        const header = headers[colIndex];
        const value = row[colIndex];
        
        if (header) {
          rowObject[header] = value;
          if (value !== undefined && value !== null && value !== '') {
            hasValidData = true;
          }
        }
      }

      if (!hasValidData && skipEmptyRows) {
        continue;
      }

      // Check for duplicates
      if (!this.config.allowDuplicates) {
        const rowKey = JSON.stringify(rowObject);
        if (seenRows.has(rowKey)) {
          duplicates++;
          errors.push({
            row: rowIndex + 2, // +2 because 1-indexed and headers
            column: 'ALL',
            value: rowObject,
            message: 'Duplicate row detected',
            severity: 'warning'
          });
          continue;
        }
        seenRows.add(rowKey);
      }

      // Validate against rules
      for (const rule of this.config.validationRules) {
        const value = rowObject[rule.columnName];
        const validationError = this.validateCell(value, rule, rowIndex + 2, rule.columnName);
        
        if (validationError) {
          errors.push(validationError);
          if (validationError.severity === 'error') {
            hasErrors = true;
          }
        }
      }

      // Count valid/invalid rows
      if (hasErrors && strictValidation) {
        invalidRows++;
      } else {
        validRows++;
        processedData.push(rowObject);
      }
    }

    return {
      errors,
      validRows,
      invalidRows,
      duplicates,
      processedData
    };
  }

  private validateCell(value: any, rule: ShopeeDocValidationRule, row: number, column: string): ValidationError | null {
    // Required field validation
    if (rule.required && (value === undefined || value === null || value === '')) {
      return {
        row,
        column,
        value,
        message: `Required field is empty`,
        severity: 'error'
      };
    }

    if (value === undefined || value === null || value === '') {
      return null; // Skip validation for empty optional fields
    }

    // Data type validation
    switch (rule.dataType) {
      case 'number':
        if (isNaN(Number(value))) {
          return {
            row,
            column,
            value,
            message: `Expected number, got: ${typeof value}`,
            severity: 'error'
          };
        }
        
        const numValue = Number(value);
        if (rule.minValue !== undefined && numValue < rule.minValue) {
          return {
            row,
            column,
            value,
            message: `Value ${numValue} is below minimum ${rule.minValue}`,
            severity: 'error'
          };
        }
        
        if (rule.maxValue !== undefined && numValue > rule.maxValue) {
          return {
            row,
            column,
            value,
            message: `Value ${numValue} exceeds maximum ${rule.maxValue}`,
            severity: 'error'
          };
        }
        break;

      case 'string':
        const strValue = String(value);
        if (rule.maxLength && strValue.length > rule.maxLength) {
          return {
            row,
            column,
            value,
            message: `Text length ${strValue.length} exceeds maximum ${rule.maxLength}`,
            severity: 'warning'
          };
        }
        
        if (rule.pattern && !rule.pattern.test(strValue)) {
          return {
            row,
            column,
            value,
            message: `Value does not match required pattern`,
            severity: 'error'
          };
        }
        break;

      case 'email':
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(String(value))) {
          return {
            row,
            column,
            value,
            message: `Invalid email format`,
            severity: 'error'
          };
        }
        break;

      case 'date':
        if (isNaN(Date.parse(String(value)))) {
          return {
            row,
            column,
            value,
            message: `Invalid date format`,
            severity: 'error'
          };
        }
        break;
    }

    return null;
  }

  private isEmptyRow(row: any[]): boolean {
    return row.every(cell => cell === undefined || cell === null || cell === '');
  }

  onDocumentTypeChange(): void {
    const newType = this.uploadForm.get('documentType')?.value;
    if (newType) {
      this.config.documentType = newType;
      this.setDefaultValidationRules();
      
      // Reset current results if document type changed
      if (this.processedResult) {
        this.processedResult = null;
        this.selectedFile = null;
      }
    }
  }

  downloadTemplate(): void {
    // Handle LM template separately
    if (this.config.documentType === 'lm') {
      this.downloadLMTemplate();
      return;
    }
    
    const template = this.getTemplateForDocumentType(this.config.documentType);
    if (template) {
      this.downloadTemplateFile(template);
      this.templateDownloaded.emit(template.documentType);
    }
  }

  private downloadLMTemplate(): void {
    this.excelImportService.getLMTemplate().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'LM-Delivery-Template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.templateDownloaded.emit('lm');
      },
      error: (error) => {
        this.error = 'Failed to download LM template: ' + error.message;
      }
    });
  }

  private downloadTemplateFile(template: ShopeeFormatTemplate): void {
    const headers = template.requiredColumns;
    const sampleData = template.sampleData;
    
    // Create workbook with multiple sheets for comprehensive template
    const wb = XLSX.utils.book_new();
    
    // Main data sheet
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    
    // Instructions sheet
    const instructionsData = [
      ['Shopee Document Upload Template'],
      ['Document Type:', template.documentType],
      ['Version:', template.version],
      ['Description:', template.description],
      [''],
      ['REQUIRED COLUMNS:'],
      ...template.requiredColumns.map(col => [`- ${col}`]),
      [''],
      ['INSTRUCTIONS:'],
      ['1. Fill in the "Data" sheet with your information'],
      ['2. Do not modify the column headers'],
      ['3. Ensure all required fields are filled'],
      ['4. Save and upload the file'],
      [''],
      ['COLUMN SPECIFICATIONS:'],
      ...this.getColumnSpecifications(template.documentType)
    ];
    const instructionsWs = XLSX.utils.aoa_to_sheet(instructionsData);
    XLSX.utils.book_append_sheet(wb, instructionsWs, 'Instructions');
    
    // Download
    const filename = `shopee-${template.documentType}-template-v${template.version}.xlsx`;
    XLSX.writeFile(wb, filename);
  }

  private getColumnSpecifications(documentType: string): string[][] {
    const specs: { [key: string]: string[][] } = {
      'order': [
        ['Order ID', 'Unique identifier for each order (max 50 characters)'],
        ['Product Name', 'Name of the product being shipped'],
        ['Quantity', 'Number of items (must be greater than 0)'],
        ['Price', 'Unit price in local currency (must be >= 0)']
      ],
      'shipment': [
        ['Tracking Number', 'Unique tracking identifier (max 30 characters)'],
        ['Weight', 'Package weight in KG (0.1 - 30000)'],
        ['Dimensions', 'Length x Width x Height in CM']
      ],
      'manifest': [
        ['Manifest ID', 'Unique manifest identifier'],
        ['Container Number', 'Container or package identifier'],
        ['Weight', 'Total weight in KG']
      ]
    };
    return specs[documentType] || [];
  }
  
  // === API UPLOAD METHODS ===
  
  private processWithAPI(file: File): void {
    if (!this.selectedFile) return;
    
    this.isUploading = true;
    this.error = null;
    
    const documentType = this.uploadForm.get('documentType')?.value;
    
    // Use LM-specific endpoint for LM files
    if (documentType === 'lm') {
      this.processLMFileWithAPI(file);
      return;
    }
    
    const additionalParams = {
      validateData: this.uploadForm.get('validateData')?.value,
      skipEmptyRows: this.uploadForm.get('skipEmptyRows')?.value,
      strictValidation: this.uploadForm.get('strictValidation')?.value,
      notes: this.uploadForm.get('notes')?.value
    };
    
    this.excelImportService.uploadExcelFile(file, documentType, additionalParams).pipe(
      takeUntil(this.destroy$),
      switchMap(job => {
        this.currentJob = job;
        this.apiJobStarted.emit(job);
        
        // Start polling for status updates
        return this.pollJobStatus(job.jobId);
      }),
      catchError(error => {
        this.error = error.message;
        this.isUploading = false;
        this.uploadError.emit(error.message);
        return [];
      })
    ).subscribe({
      next: (finalJob) => {
        if (finalJob) {
          this.currentJob = finalJob;
          this.handleAPIJobCompletion(finalJob);
        }
      },
      error: (error) => {
        this.error = error.message;
        this.isUploading = false;
        this.uploadError.emit(error.message);
      }
    });
  }
  
  private processLMFileWithAPI(file: File): void {
    // For LM files, we need pre-alert data first
    if (!this.lmPreviewData) {
      this.error = 'Pre-alert data required for LM file processing';
      this.isUploading = false;
      this.uploadError.emit(this.error);
      return;
    }
    
    // Call the correct LM endpoint using the existing service method
    this.excelImportService.importLMFileWithPreAlert(file, this.lmPreviewData).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        this.error = error.message;
        this.isUploading = false;
        this.uploadError.emit(error.message);
        return [];
      })
    ).subscribe({
      next: (result) => {
        this.handleLMImportCompletion(result);
      },
      error: (error) => {
        this.error = error.message;
        this.isUploading = false;
        this.uploadError.emit(error.message);
      }
    });
  }

  private handleLMImportCompletion(result: any): void {
    this.isUploading = false;
    this.isPollingStatus = false;
    
    // Debug logging for LM import completion
    console.log('EXCEL_UPLOAD_DEBUG: LM import completion handling, full result:', result);
    
    // Extract and log pre-alert data if available
    const preAlertData = result?.resultData?.preAlertData;
    if (preAlertData) {
      console.log('EXCEL_UPLOAD_DEBUG: Pre-alert data extracted from result:', preAlertData);
      console.log('EXCEL_UPLOAD_DEBUG: Pre-alert validation:');
      console.log(`  ✓ Lading Bill: ${preAlertData.ladingBill || 'MISSING'}`);
      console.log(`  ✓ Departure: ${preAlertData.departureLocation || 'MISSING'}`);
      console.log(`  ✓ Arrival: ${preAlertData.arrivalLocation || 'MISSING'}`);
      console.log(`  ✓ ETD: ${preAlertData.etd || 'MISSING'}`);
      console.log(`  ✓ ETA: ${preAlertData.eta || 'MISSING'}`);
      console.log(`  ✓ Weight: ${preAlertData.weight || 'MISSING'}`);
      console.log(`  ✓ Transport Company: ${preAlertData.transportCompanyName || 'MISSING'}`);
      console.log(`  ✓ Vessel: ${preAlertData.vesselNo || 'MISSING'}`);
      console.log(`  ✓ Pieces: ${preAlertData.pieces || 'MISSING'}`);
    } else {
      console.warn('EXCEL_UPLOAD_DEBUG: No pre-alert data found in completion result');
    }
    
    // Convert LM import result to ProcessedUploadResult for compatibility
    const processedResult: ProcessedUploadResult = {
      filename: this.selectedFile?.name || '',
      data: [],
      headers: [],
      rowCount: result.totalRows || 0,
      validationErrors: result.errors || [],
      validRowCount: result.successfulRows || 0,
      invalidRowCount: result.errorRows || 0,
      duplicateCount: 0,
      processedData: [],
      summary: {
        totalRows: result.totalRows || 0,
        successRate: result.successRate || 100,
        warnings: result.warnings?.length || 0,
        errors: result.errors?.length || 0
      }
    };
    
    this.processedResult = processedResult;
    
    // Emit events for compatibility
    this.validationCompleted.emit(processedResult);
    this.uploadCompleted.emit(processedResult);
    
    // Save to history
    this.saveToUploadHistory(processedResult);
  }
  
  private pollJobStatus(jobId: string): Observable<ImportJobDto> {
    this.isPollingStatus = true;
    
    return this.excelImportService.getJobStatus(jobId, true).pipe(
      takeUntil(this.destroy$),
      switchMap(status => {
        this.jobStatus = status;
        
        // Update progress based on status
        if (status.progress) {
          this.currentProgress = {
            percentage: status.progress.percentage,
            stage: status.progress.stage as any,
            message: status.progress.message || ''
          };
        }
        
        // If job is complete, get final job details
        if (status.status === 'completed' || status.status === 'error' || status.status === 'cancelled') {
          return this.excelImportService.getJobDetails(jobId);
        }
        
        return [];
      }),
      catchError(error => {
        this.isPollingStatus = false;
        throw error;
      })
    );
  }
  
  private handleAPIJobCompletion(job: ImportJobDto): void {
    this.isUploading = false;
    this.isPollingStatus = false;
    
    if (job.status === 'completed') {
      // Convert to ProcessedUploadResult for compatibility
      const processedResult: ProcessedUploadResult = {
        filename: job.filename,
        data: job.resultData || [],
        headers: [], // Will be populated from result data
        rowCount: job.totalRows || 0,
        validationErrors: [],
        validRowCount: job.processedRows || 0,
        invalidRowCount: job.errorRows || 0,
        duplicateCount: 0,
        processedData: job.resultData || [],
        summary: {
          totalRows: job.totalRows || 0,
          successRate: job.successRate || 0,
          warnings: job.warningRows || 0,
          errors: job.errorRows || 0
        }
      };
      
      this.processedResult = processedResult;
      this.saveApiJobToHistory(job);
      
      // Emit events for compatibility
      this.validationCompleted.emit(processedResult);
      this.uploadCompleted.emit(processedResult);
      this.apiJobCompleted.emit(job);
      
    } else if (job.status === 'error') {
      this.error = job.errorMessage || 'Processing failed';
      this.uploadError.emit(this.error || 'Unknown error');
    }
  }
  
  private saveApiJobToHistory(job: ImportJobDto): void {
    try {
      const historyItem: UploadHistoryItem = {
        id: job.jobId,
        filename: job.filename,
        documentType: job.documentType,
        uploadedAt: new Date(job.uploadedAt),
        uploadedBy: job.uploadedBy,
        totalRows: job.totalRows || 0,
        validRows: job.processedRows || 0,
        status: job.successRate && job.successRate >= 80 ? 'success' : 
               job.successRate && job.successRate >= 50 ? 'warning' : 'error',
        processingTime: job.processingTimeMs || 0,
        fileSize: job.fileSize
      };
      
      this.uploadHistory.unshift(historyItem);
      this.uploadHistory = this.uploadHistory.slice(0, this.maxHistoryItems);
      
      // Save to localStorage
      localStorage.setItem('shopee_upload_history', JSON.stringify(
        this.uploadHistory.map(item => ({
          ...item,
          uploadedAt: item.uploadedAt.toISOString()
        }))
      ));
    } catch (error) {
      console.warn('Failed to save API job to history:', error);
    }
  }
  
  cancelAPIJob(): void {
    if (!this.currentJob || !this.canCancelApiJob) return;
    
    this.excelImportService.cancelJob(this.currentJob.jobId).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Failed to cancel job:', error);
        return [];
      })
    ).subscribe(result => {
      if (result && result.success) {
        this.stopPolling();
        this.resetUpload();
        this.error = null;
      }
    });
  }
  
  retryAPIJob(): void {
    if (!this.currentJob) return;
    
    this.error = null;
    this.isUploading = true;
    
    this.excelImportService.retryJob(this.currentJob.jobId).pipe(
      takeUntil(this.destroy$),
      switchMap(job => {
        this.currentJob = job;
        return this.pollJobStatus(job.jobId);
      }),
      catchError(error => {
        this.error = error.message;
        this.isUploading = false;
        return [];
      })
    ).subscribe(finalJob => {
      if (finalJob) {
        this.currentJob = finalJob;
        this.handleAPIJobCompletion(finalJob);
      }
    });
  }
  
  downloadAPIResults(): void {
    if (!this.currentJob || this.currentJob.status !== 'completed') return;
    
    this.excelImportService.downloadResults(this.currentJob.jobId).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Failed to download results:', error);
        return [];
      })
    ).subscribe(blob => {
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.currentJob?.filename}_processed.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    });
  }
  
  downloadAPIErrors(): void {
    if (!this.currentJob || this.currentJob.status !== 'completed') return;
    
    this.excelImportService.downloadErrorReport(this.currentJob.jobId).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Failed to download error report:', error);
        return [];
      })
    ).subscribe(blob => {
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.currentJob?.filename}_errors.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    });
  }
  
  private stopPolling(): void {
    this.isPollingStatus = false;
  }

  private generateSampleData(): any[][] {
    const sampleRows: any[][] = [];
    
    for (let i = 0; i < 3; i++) {
      const row: any[] = [];
      for (const column of this.config.requiredColumns) {
        // Generate sample data based on column name
        switch (column.toLowerCase()) {
          case 'order id':
            row.push(`ORD${String(i + 1).padStart(6, '0')}`);
            break;
          case 'product name':
            row.push(`Sample Product ${i + 1}`);
            break;
          case 'quantity':
            row.push(Math.floor(Math.random() * 10) + 1);
            break;
          case 'price':
            row.push((Math.random() * 100 + 10).toFixed(2));
            break;
          case 'tracking number':
            row.push(`TN${Date.now()}${i}`);
            break;
          case 'weight':
            row.push((Math.random() * 5 + 0.5).toFixed(2));
            break;
          default:
            row.push(`Sample ${i + 1}`);
        }
      }
      sampleRows.push(row);
    }
    
    return sampleRows;
  }

  resetUpload(): void {
    this.selectedFile = null;
    this.processedResult = null;
    this.error = null;
    this.successMessage = null;
    this.isUploading = false;
    this.currentProgress = null;
    this.showErrorDetails = false;
    this.uploadStartTime = null;
    this.processingStartTime = null;
    this.dragOverCounter = 0;
    
    // Clear API state
    this.currentJob = null;
    this.jobStatus = null;
    this.apiProgress = null;
    this.stopPolling();
    this.excelImportService.clearProgress();
  }

  exportValidData(): void {
    if (!this.processedResult) return;

    const ws = XLSX.utils.json_to_sheet(this.processedResult.processedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Valid Data');
    
    const filename = `shopee-${this.config.documentType}-valid-data.xlsx`;
    XLSX.writeFile(wb, filename);
  }

  exportErrors(): void {
    if (!this.processedResult || this.processedResult.validationErrors.length === 0) return;

    const errorData = this.processedResult.validationErrors.map(error => ({
      Row: error.row,
      Column: error.column,
      Value: error.value,
      Message: error.message,
      Severity: error.severity
    }));

    const ws = XLSX.utils.json_to_sheet(errorData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Validation Errors');
    
    const filename = `shopee-${this.config.documentType}-errors.xlsx`;
    XLSX.writeFile(wb, filename);
  }

  // Getters for template
  get canUpload(): boolean {
    return !this.disabled && !this.isUploading && !this.isPollingStatus;
  }

  get hasResults(): boolean {
    return !!this.processedResult || (!!this.currentJob && this.currentJob.status === 'completed');
  }

  get hasErrors(): boolean {
    return !!this.processedResult && this.processedResult.validationErrors.length > 0;
  }

  get errorCount(): number {
    return this.processedResult?.validationErrors.filter(e => e.severity === 'error').length || 0;
  }

  get warningCount(): number {
    return this.processedResult?.validationErrors.filter(e => e.severity === 'warning').length || 0;
  }

  // === TEMPLATE MANAGEMENT ===

  private initializeTemplates(): void {
    this.availableTemplates = [
      {
        documentType: 'order',
        requiredColumns: ['Order ID', 'Product Name', 'Quantity', 'Price', 'Customer Name', 'Address'],
        sampleData: [
          ['ORD000001', 'Sample Product 1', 2, 29.99, 'John Doe', '123 Main St'],
          ['ORD000002', 'Sample Product 2', 1, 49.99, 'Jane Smith', '456 Oak Ave'],
          ['ORD000003', 'Sample Product 3', 3, 19.99, 'Bob Johnson', '789 Pine Rd']
        ],
        description: 'Template for Shopee order data import with customer and product information',
        version: '2.1'
      },
      {
        documentType: 'shipment',
        requiredColumns: ['Tracking Number', 'Weight', 'Dimensions', 'Origin', 'Destination', 'Service Type'],
        sampleData: [
          ['TN123456789', 1.5, '30x20x10', 'Manila', 'Cebu', 'Express'],
          ['TN123456790', 2.8, '40x30x15', 'Davao', 'Quezon City', 'Standard'],
          ['TN123456791', 0.5, '20x15x5', 'Iloilo', 'Baguio', 'Economy']
        ],
        description: 'Template for shipment tracking and logistics information',
        version: '1.8'
      },
      {
        documentType: 'manifest',
        requiredColumns: ['Manifest ID', 'Container Number', 'Weight', 'Items Count', 'Seal Number'],
        sampleData: [
          ['MAN2025001', 'CONT123456', 1500.0, 25, 'SEAL001'],
          ['MAN2025002', 'CONT123457', 2300.5, 40, 'SEAL002'],
          ['MAN2025003', 'CONT123458', 890.2, 15, 'SEAL003']
        ],
        description: 'Template for customs manifest and container documentation',
        version: '3.2'
      },
      {
        documentType: 'lm',
        requiredColumns: ['Order ID', 'Tracking Number', 'Weight (kg)', 'Recipient', 'Address', 'Phone Number'],
        sampleData: [
          ['ORD-2025-001', 'TN123456789', 2.5, 'John Doe', '123 Main St, Manila', '+63 912 345 6789'],
          ['ORD-2025-002', 'TN123456790', 1.8, 'Jane Smith', '456 Oak Ave, Cebu', '+63 918 765 4321'],
          ['ORD-2025-003', 'TN123456791', 0.9, 'Bob Wilson', '789 Pine St, Davao', '+63 917 111 2222']
        ],
        description: 'Template for Last Mile (LM) delivery data with pre-alert integration',
        version: '1.0'
      }
    ];
  }

  private getTemplateForDocumentType(documentType: string): ShopeeFormatTemplate | null {
    return this.availableTemplates.find(t => t.documentType === documentType) || null;
  }

  selectTemplate(template: ShopeeFormatTemplate): void {
    this.selectedTemplate = template;
    this.config.documentType = template.documentType as any;
    this.config.requiredColumns = template.requiredColumns;
    this.setDefaultValidationRules();
    this.uploadForm.patchValue({ documentType: template.documentType });
  }

  // === UPLOAD HISTORY MANAGEMENT ===

  private loadUploadHistory(): void {
    try {
      const stored = localStorage.getItem('shopee_upload_history');
      if (stored) {
        const history = JSON.parse(stored).map((item: any) => ({
          ...item,
          uploadedAt: new Date(item.uploadedAt)
        }));
        this.uploadHistory = history.slice(0, this.maxHistoryItems);
      }
    } catch (error) {
      console.warn('Failed to load upload history:', error);
    }
  }

  private saveToUploadHistory(result: ProcessedUploadResult): void {
    try {
      const currentUser = this.authService.getCurrentUser();
      const processingTime = this.uploadStartTime && this.processingStartTime 
        ? this.processingStartTime.getTime() - this.uploadStartTime.getTime()
        : 0;
      
      const historyItem: UploadHistoryItem = {
        id: this.generateHistoryId(),
        filename: result.filename,
        documentType: this.config.documentType,
        uploadedAt: new Date(),
        uploadedBy: currentUser?.username || 'Unknown',
        totalRows: result.summary.totalRows,
        validRows: result.validRowCount,
        status: result.summary.errors === 0 ? 'success' : 
                result.summary.successRate >= 80 ? 'warning' : 'error',
        processingTime,
        fileSize: this.selectedFile?.size || 0
      };
      
      this.uploadHistory.unshift(historyItem);
      
      // Keep only latest items
      this.uploadHistory = this.uploadHistory.slice(0, this.maxHistoryItems);
      
      // Save to localStorage
      localStorage.setItem('shopee_upload_history', JSON.stringify(
        this.uploadHistory.map(item => ({
          ...item,
          uploadedAt: item.uploadedAt.toISOString()
        }))
      ));
    } catch (error) {
      console.warn('Failed to save upload history:', error);
    }
  }

  private generateHistoryId(): string {
    return 'hist_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // === LM FILE PREVIEW AND PRE-ALERT METHODS ===

  private async handleLMFilePreview(file: File): Promise<void> {
    try {
      this.isUploading = true;
      this.currentProgress = {
        percentage: 25,
        stage: 'reading',
        message: 'Reading LM file for preview...'
      };

      // Read and validate LM file
      const previewData = await this.previewLMFile(file);

      if (!previewData.validationPassed) {
        this.error = 'LM file validation failed: ' + previewData.errors.join(', ');
        this.isUploading = false;
        return;
      }

      // Check for duplicate pre-alert before showing modal
      this.currentProgress = {
        percentage: 75,
        stage: 'validating',
        message: 'Checking for existing pre-alert...'
      };

      const ladingBillFromFilename = this.cleanFilenameForLadingBill(file.name);
      if (ladingBillFromFilename) {
        try {
          const exists = await firstValueFrom(this.preAlertService.checkPreAlertExists(ladingBillFromFilename));
          if (exists) {
            this.error = `⚠️ Pre-Alert "${ladingBillFromFilename}" already exists in the system!\n\n` +
                        `This pre-alert was likely created via API integration.\n` +
                        `Uploading this file would overwrite existing data.\n\n` +
                        `Please:\n` +
                        `• Use a different filename for a new pre-alert, OR\n` +
                        `• Contact admin if you need to update the existing pre-alert`;
            this.isUploading = false;
            return;
          }
        } catch (error) {
          // If check fails, log error but continue (fail-open for now)
          console.warn('Failed to check pre-alert existence:', error);
        }
      }

      this.currentProgress = {
        percentage: 100,
        stage: 'complete',
        message: `Preview ready: ${previewData.totalRows} rows found`
      };

      // Store preview data and show pre-alert modal
      this.lmPreviewData = previewData;
      this.showPreAlertModal = true;
      this.isUploading = false;
      this.preAlertRequested.emit(previewData);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to preview LM file';
      this.error = errorMessage;
      this.isUploading = false;
      this.uploadError.emit(errorMessage);
    }
  }

  /**
   * Clean filename to extract potential lading bill
   * Same logic as in PreAlertInputModalComponent
   */
  private cleanFilenameForLadingBill(filename: string): string {
    if (!filename) return '';

    // Remove file extension
    const cleanName = filename
      .replace(/\.(xlsx|xls|csv|xlsm|xlsb)$/i, '')
      .trim();

    // Replace underscores and spaces with hyphens
    const normalized = cleanName
      .replace(/[_\s]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');

    // Sanitize to alphanumeric, hyphens, underscores only
    const sanitized = normalized.replace(/[^A-Z0-9\-_]/gi, '');

    // Limit length
    return sanitized.substring(0, 50);
  }

  private async previewLMFile(file: File): Promise<LMPreviewData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON with header row
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length < 2) {
            reject(new Error('LM file must contain at least a header row and one data row'));
            return;
          }
          
          const headers = jsonData[0] as string[];
          const rows = jsonData.slice(1).map((row: any) => row as any[]);
          
          // Validate LM headers
          const validation = this.validateLMHeaders(headers);
          
          // Count unique cartons if validation passed
          let uniqueCartonCount = 0;
          if (validation.isValid) {
            uniqueCartonCount = this.countUniqueCartons(headers, rows);
          }
          
          const previewData: LMPreviewData = {
            headers,
            rows,
            totalRows: rows.length,
            filename: file.name,
            validationPassed: validation.isValid,
            errors: validation.isValid ? [] : [validation.message],
            uniqueCartonCount
          };
          
          resolve(previewData);
        } catch (error) {
          reject(new Error('Failed to read Excel file: ' + (error instanceof Error ? error.message : 'Unknown error')));
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  private validateLMHeaders(headers: string[]): { isValid: boolean; message: string } {
    const normalizedHeaders = headers.map(h => h?.toLowerCase().trim());
    
    // Updated to match the user's exact header format and backend expectations
    const requiredLMColumns = [
      { pattern: 'lm tracking', alternatives: ['lmtracking', 'lm_tracking'] },
      { pattern: 'carton no', alternatives: ['cartonnumber', 'carton_no', 'carton number'] },
      { pattern: 'carton weight', alternatives: ['cartonweight', 'carton_weight'] }
    ];
    
    const missingColumns: string[] = [];
    
    for (const required of requiredLMColumns) {
      const found = normalizedHeaders.some(header => {
        if (!header) return false;
        
        // Check exact match
        if (header === required.pattern) return true;
        
        // Check alternatives
        return required.alternatives.some(alt => 
          header === alt || header.includes(alt.replace('_', ' ')) || header.includes(alt.replace(' ', ''))
        );
      });
      
      if (!found) {
        missingColumns.push(required.pattern);
      }
    }

    if (missingColumns.length > 0) {
      return {
        isValid: false,
        message: `Missing required LM columns: ${missingColumns.join(', ')}. Expected headers include: LM Tracking, Carton No, Carton Weight, etc.`
      };
    }

    return { isValid: true, message: '' };
  }

  private findCartonColumnIndex(headers: string[]): number {
    const normalizedHeaders = headers.map(h => h?.toLowerCase().trim());
    
    // Look for carton column patterns (flexible matching)
    const cartonPatterns = [
      'carton no', 'carton number', 'cartonnumber', 'carton_no', 'carton_number',
      'carton', 'cartons', 'box no', 'box number', 'package no', 'package number'
    ];
    
    for (let i = 0; i < normalizedHeaders.length; i++) {
      const header = normalizedHeaders[i];
      if (!header) continue;
      
      for (const pattern of cartonPatterns) {
        if (header === pattern || header.includes(pattern.replace('_', ' ')) || header.includes(pattern.replace(' ', ''))) {
          return i;
        }
      }
    }
    
    return -1; // Not found
  }

  private countUniqueCartons(headers: string[], rows: any[][]): number {
    const cartonColumnIndex = this.findCartonColumnIndex(headers);
    
    if (cartonColumnIndex === -1) {
      // No carton column found, return 0 to indicate unknown
      return 0;
    }
    
    const uniqueCartons = new Set<string>();
    
    for (const row of rows) {
      const cartonValue = row[cartonColumnIndex];
      
      // Skip empty values
      if (cartonValue !== undefined && cartonValue !== null && cartonValue !== '') {
        // Convert to string and trim whitespace for consistent comparison
        const cartonString = String(cartonValue).trim();
        if (cartonString.length > 0) {
          uniqueCartons.add(cartonString);
        }
      }
    }
    
    return uniqueCartons.size;
  }

  onPreAlertModalResult(result: PreAlertModalResult): void {
    this.showPreAlertModal = false;
    this.isPreAlertLoading = false;
    
    if (result.action === 'save' && result.data && this.lmPreviewData) {
      // Proceed with LM file processing with pre-alert data
      this.processLMFileWithPreAlert(result.data, this.lmPreviewData);
      this.preAlertSaved.emit({
        preAlert: result.data,
        fileData: this.lmPreviewData
      });
    } else {
      // User cancelled - reset upload state
      this.resetUpload();
    }
  }

  private async processLMFileWithPreAlert(preAlertData: PreAlertInputData, fileData: LMPreviewData): Promise<void> {
    try {
      this.isUploading = true;
      this.successMessage = null; // Clear any previous success message
      this.currentProgress = {
        percentage: 25,
        stage: 'processing',
        message: 'Uploading LM file with pre-alert data...'
      };
      
      // Debug: Log the pre-alert data being sent
      console.log('EXCEL_UPLOAD_DEBUG: Starting LM file processing with pre-alert data:', preAlertData);
      console.log('EXCEL_UPLOAD_DEBUG: File data preview:', fileData);
      console.log('EXCEL_UPLOAD_DEBUG: Selected file info:', {
        name: this.selectedFile?.name,
        size: this.selectedFile?.size,
        type: this.selectedFile?.type
      });
      
      // Call the actual backend API
      if (!this.selectedFile) {
        throw new Error('No file selected');
      }
      
      // Use the existing service method to call backend
      this.excelImportService.importLMFileWithPreAlert(this.selectedFile, preAlertData).pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          this.error = error.message;
          this.isUploading = false;
          this.uploadError.emit(error.message);
          return [];
        })
      ).subscribe({
        next: (result) => {
          // Handle successful import
          this.handleLMImportSuccess(result, fileData);
        },
        error: (error) => {
          this.error = error.message;
          this.isUploading = false;
          this.uploadError.emit(error.message);
        }
      });
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process LM file';
      this.error = errorMessage;
      this.isUploading = false;
      this.uploadError.emit(errorMessage);
    }
  }

  private handleLMImportSuccess(result: any, fileData: LMPreviewData): void {
    this.isUploading = false;
    
    // Create processed result for display
    const processedResult: ProcessedUploadResult = {
      filename: fileData.filename,
      data: fileData.rows,
      headers: fileData.headers,
      rowCount: result.totalRows || fileData.totalRows,
      validationErrors: [],
      validRowCount: result.successfulRows || fileData.totalRows,
      invalidRowCount: result.errorRows || 0,
      duplicateCount: 0,
      processedData: fileData.rows.map(row => {
        const obj: any = {};
        fileData.headers.forEach((header, index) => {
          obj[header] = row[index];
        });
        return obj;
      }),
      summary: {
        totalRows: result.totalRows || fileData.totalRows,
        successRate: result.successRate || 100,
        warnings: result.warnings?.length || 0,
        errors: result.errors?.length || 0
      }
    };
    
    this.processedResult = processedResult;
    
    this.currentProgress = {
      percentage: 100,
      stage: 'complete',
      message: `Successfully imported ${result.ordersCreated || 0} orders and created pre-alert #${result.preAlertId || 'N/A'}`
    };
    
    // Save to history
    this.saveToUploadHistory(processedResult);
    
    // Emit events
    this.validationCompleted.emit(processedResult);
    this.uploadCompleted.emit(processedResult);
    
    // Show success message
    this.showSuccessNotification(result);
    
    // Navigate to appropriate view after delay
    setTimeout(() => {
      if (result.preAlertId) {
        // Navigate to pre-alerts list
        this.router.navigate(['/dashboard/pre-alerts']);
      } else {
        // Navigate to orders list
        this.router.navigate(['/dashboard/orders']);
      }
    }, 3000);
  }

  private showSuccessNotification(result: any): void {
    const message = `Import successful! Created ${result.ordersCreated || 0} orders and pre-alert #${result.preAlertId || 'N/A'}`;
    
    // Set success message for template display
    this.successMessage = message;
    console.log(message);
  }

  // Helper method to show LM preview in modal
  showLMPreview(): void {
    if (this.lmPreviewData) {
      this.showPreAlertModal = true;
      this.preAlertModalMode = 'create';
    }
  }

  // Helper method to edit pre-alert data
  editPreAlert(): void {
    if (this.processedResult?.requiresPreAlert) {
      this.showPreAlertModal = true;
      this.preAlertModalMode = 'edit';
    }
  }

  onHistoryItemClick(item: UploadHistoryItem): void {
    this.historyItemClicked.emit(item);
  }

  clearUploadHistory(): void {
    this.uploadHistory = [];
    try {
      localStorage.removeItem('shopee_upload_history');
    } catch (error) {
      console.warn('Failed to clear upload history:', error);
    }
  }

  // === UTILITY AND DISPLAY METHODS ===

  getPreviewCount(): number {
    if (!this.processedResult) return 0;
    return Math.min(this.maxPreviewRows, this.processedResult.validRowCount);
  }

  getProcessingTime(): string {
    if (this.currentJob && this.currentJob.processingTimeMs) {
      return `${(this.currentJob.processingTimeMs / 1000).toFixed(1)}s`;
    }
    if (!this.uploadStartTime || !this.processingStartTime) return 'N/A';
    const diffMs = this.processingStartTime.getTime() - this.uploadStartTime.getTime();
    const seconds = (diffMs / 1000).toFixed(1);
    return `${seconds}s`;
  }

  // Getters for template
  get hasUploadHistory(): boolean {
    return this.uploadHistory.length > 0;
  }

  get canShowHistory(): boolean {
    return this.showUploadHistory && this.hasUploadHistory;
  }

  get recentUploadHistory(): UploadHistoryItem[] {
    return this.uploadHistory.slice(0, 5); // Show only 5 most recent
  }

  get isDragSupported(): boolean {
    return 'draggable' in document.createElement('div') && 'ondrop' in window;
  }
  
  get isApiJobActive(): boolean {
    return !!this.currentJob && (this.currentJob.status === 'pending' || this.currentJob.status === 'processing');
  }
  
  get canCancelApiJob(): boolean {
    return this.isApiJobActive && !this.isPollingStatus;
  }
  
  get apiJobStatusText(): string {
    if (!this.currentJob) return 'No job active';
    switch (this.currentJob.status) {
      case 'pending': return 'Queued for processing';
      case 'processing': return 'Processing file';
      case 'completed': return 'Processing complete';
      case 'error': return 'Processing failed';
      case 'cancelled': return 'Job cancelled';
      default: return this.currentJob.status;
    }
  }
}